/* ============================================
   Portal Cliente — Vista Pública de Cotización
   Accesible por public_token sin autenticación
   OCULTA: márgenes, costos internos
   ============================================ */
import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Cotizacion, Empresa, CotizacionItemsEstandar, CotizacionItemsEquipo, EquipoMiembro } from '@/lib/types'
import { formatCurrency, formatDate, getDiscountTier, processContent } from '@/lib/utils'

/** FTE label for equipo dedicado */
function fmtFTE(m: EquipoMiembro): string {
  if (m.dedicacion_fte === 1.0) return 'Full-Time'
  if (m.dedicacion_fte === 0.5) return 'Part-Time'
  if (m.dedicacion_fte === 0.25) return 'Quarter-Time'
  return `${m.horas_custom}h/mes`
}

export default function PortalCliente() {
  const { token } = useParams<{ token: string }>()
  const pdfRef = useRef<HTMLDivElement>(null)

  const [cot, setCot] = useState<Cotizacion | null>(null)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        // Anon query — RLS enforces token match
        const { data: cotArr, error: cotErr } = await supabase
          .from('cotizaciones')
          .select('*')
          .eq('public_token', token!)
          .limit(1)

        if (cotErr) throw cotErr
        if (!cotArr || cotArr.length === 0) { setError('Cotización no encontrada o enlace inválido.'); return }

        const { data: empArr } = await supabase.from('empresa').select('*').limit(1)
        setCot(cotArr[0] as unknown as Cotizacion)
        setEmpresa((empArr?.[0] ?? null) as unknown as Empresa)
      } catch { setError('No se pudo cargar la cotización.') }
      finally { setLoading(false) }
    })()
  }, [token])

  const exportPDF = async () => {
    if (!pdfRef.current || !cot) return
    setExporting(true)
    try {
      const html2canvas = (await import('html2canvas')).default
      const { jsPDF } = await import('jspdf')
      const pages = pdfRef.current.querySelectorAll('.cotizacion-preview')
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage()
        const canvas = await html2canvas(pages[i] as HTMLElement, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false })
        const ratio = canvas.width / canvas.height
        const imgW = 210; const imgH = Math.min(imgW / ratio, 297)
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, imgW, imgH)
      }
      pdf.save(`${cot.proyecto_nombre || 'Cotizacion'} - ${cot.cliente_nombre || cot.numero}.pdf`)
    } catch { /* silent */ }
    finally { setExporting(false) }
  }

  // ─── Loading state ───
  if (loading) return (
    <div className="portal-wrapper">
      <div className="portal-loading">
        <div className="auth-spinner" style={{ width: 40, height: 40, margin: '0 auto 16px' }} />
        <p>Cargando cotización…</p>
      </div>
    </div>
  )

  // ─── Error state ───
  if (error || !cot || !empresa) return (
    <div className="portal-wrapper">
      <div className="portal-error">
        <h2>⚠️ Enlace inválido</h2>
        <p>{error || 'La cotización solicitada no existe o ya no está disponible.'}</p>
        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: 12 }}>Si crees que esto es un error, contacta a tu asesor comercial.</p>
      </div>
    </div>
  )

  const isEquipo = cot.tipo_cotizacion === 'equipo_dedicado'
  const secciones = Array.isArray(cot.secciones) ? cot.secciones : []
  const pc = empresa.color_primario || '#1C4587'
  const proyectoNombre = cot.proyecto_nombre || 'Propuesta de Servicios'

  // Standard items
  const standardItems = !isEquipo && Array.isArray(cot.items) ? cot.items as CotizacionItemsEstandar[] : []

  // Equipo items (filtered — no costs/margins)
  const equipoData = isEquipo && cot.items && typeof cot.items === 'object' && 'equipo' in cot.items
    ? cot.items as CotizacionItemsEquipo : null
  const equipoMembers = equipoData?.equipo ?? []
  const equipoTotales = equipoData?.totales

  const tier = getDiscountTier(cot.descuento_porcentaje)

  return (
    <div className="portal-wrapper">
      {/* Top bar */}
      <div className="portal-topbar" style={{ background: pc }}>
        <div className="portal-topbar-inner">
          <div className="portal-brand">
            {empresa.logo_url ? <img src={empresa.logo_url} alt={empresa.nombre ?? ''} style={{ height: 32 }} /> : <strong style={{ color: '#fff' }}>{empresa.nombre}</strong>}
          </div>
          <button className="btn btn-sm portal-pdf-btn" onClick={exportPDF} disabled={exporting}>
            {exporting ? '⏳ Generando…' : '📥 Descargar PDF'}
          </button>
        </div>
      </div>

      <div className="portal-content" ref={pdfRef}>
        {/* PAGE 1: COVER */}
        <div className="cotizacion-preview">
          <div className="cot-header" style={{ background: `linear-gradient(135deg, ${pc} 0%, ${pc}cc 100%)` }}>
            <div className="cot-header-left">
              {empresa.logo_url ? <img src={empresa.logo_url} className="cot-logo" alt={empresa.nombre ?? ''} /> : <div className="cot-logo-text">{empresa.nombre || '1Conecta'}</div>}
            </div>
            <div className="cot-header-right">
              <div className="cot-header-fiscal">{empresa.razon_social || ''}</div>
              <div>RUC {empresa.ruc || ''}</div>
            </div>
          </div>

          <div className="cot-title-banner" style={{ borderLeft: `4px solid ${pc}` }}>
            <div className="cot-title-label">{isEquipo ? 'PROPUESTA DE EQUIPO DEDICADO' : 'COTIZACIÓN'}</div>
            <div className="cot-title-amount">{isEquipo ? 'INVERSIÓN MENSUAL' : 'PRESUPUESTO'}: <strong>{formatCurrency(cot.total, cot.moneda)}</strong></div>
          </div>

          <div className="cot-meta">
            <div className="cot-meta-block">
              <h4>Cotizado a</h4>
              <div className="cot-meta-grid">
                <span className="cot-meta-key">E:</span><span>{cot.cliente_nombre || '—'}</span>
                <span className="cot-meta-key">C:</span><span>{cot.cliente_contacto || '—'}</span>
                <span className="cot-meta-key">T:</span><span>{cot.cliente_telefono || '—'}</span>
                <span className="cot-meta-key">@:</span><span>{cot.cliente_email || '—'}</span>
              </div>
            </div>
            <div className="cot-meta-block" style={{ textAlign: 'right' }}>
              <h4>Detalles</h4>
              <div className="cot-meta-grid" style={{ justifyItems: 'end' }}>
                <span className="cot-meta-key">N:</span><span>{cot.numero}</span>
                <span className="cot-meta-key">F:</span><span>{formatDate(cot.fecha_emision)}</span>
                <span className="cot-meta-key">D:</span><span>Validez: {cot.validez_dias} días calendario</span>
              </div>
            </div>
          </div>

          <div className="cot-body">
            <p className="cot-intro">
              {isEquipo
                ? <>A través del presente documento, ponemos a su disposición la propuesta de <strong>{proyectoNombre}</strong>. A continuación, se detalla la composición del equipo profesional dedicado y la inversión mensual asociada.</>
                : <>A través del presente documento, ponemos a su disposición la propuesta económica correspondiente al <strong>{proyectoNombre}</strong>. En la siguiente sección encontrará el desglose detallado de la inversión.</>
              }
            </p>

            {/* TABLE: Standard items OR Equipo composition (CLIENT VIEW — no costs/margins) */}
            {isEquipo ? (
              <>
                <h3 className="cot-section-title" style={{ borderColor: pc }}>Composición del Equipo</h3>
                <table className="cot-table">
                  <thead><tr><th style={{ width: '40%' }}>Rol Profesional</th><th style={{ width: '15%' }}>Cantidad</th><th style={{ width: '25%' }}>Dedicación</th><th style={{ width: '20%' }}>Horas / Mes</th></tr></thead>
                  <tbody>
                    {equipoMembers.map((m, i) => (
                      <tr key={i}><td><strong>{m.rol_comercial}</strong> ({m.seniority})</td><td>{m.cantidad}</td><td>{fmtFTE(m)}</td><td>{m.horas_mensuales_linea}h</td></tr>
                    ))}
                    <tr style={{ background: 'var(--bg-secondary, #f5f5f5)', fontWeight: 700 }}>
                      <td colSpan={3}>Total Horas del Equipo</td>
                      <td>{equipoTotales?.total_horas_equipo ?? 0}h</td>
                    </tr>
                  </tbody>
                </table>
              </>
            ) : (
              <table className="cot-table">
                <thead><tr><th style={{ width: '50%' }}>Descripción</th><th style={{ width: '18%' }}>Costo</th><th style={{ width: '10%' }}>Cant.</th><th style={{ width: '22%' }}>Total</th></tr></thead>
                <tbody>{standardItems.map((it, i) => (
                  <tr key={i}><td>{it.descripcion}</td><td>{formatCurrency(it.precio_unitario, cot.moneda)}</td><td>{it.cantidad}</td><td>{formatCurrency(it.total || it.precio_unitario * it.cantidad, cot.moneda)}</td></tr>
                ))}</tbody>
              </table>
            )}

            <div className="cot-bottom-row">
              <div className="cot-payment-box">
                <h4>Datos para Transferencia Bancaria</h4>
                <div className="cot-bank-info">
                  <div className="cot-bank-section"><div className="cot-bank-label">Banco</div><div className="cot-bank-value">{empresa.banco || 'Interbank'}</div></div>
                  <div className="cot-bank-section"><div className="cot-bank-label">Beneficiario</div><div className="cot-bank-value">{empresa.razon_social || ''}</div></div>
                  <div className="cot-bank-section"><div className="cot-bank-label">RUC</div><div className="cot-bank-value">{empresa.ruc || ''}</div></div>
                  <div className="cot-bank-divider" />
                  <div className="cot-bank-section"><div className="cot-bank-sublabel">Depósito Nacional (USD)</div><div className="cot-bank-label">Cta. Corriente Dólares</div><div className="cot-bank-value cot-bank-mono">{empresa.cuenta_corriente || ''}</div></div>
                  <div className="cot-bank-divider" />
                  <div className="cot-bank-section"><div className="cot-bank-sublabel">Transferencia Interbancaria (CCI)</div><div className="cot-bank-label">Código CCI (Dólares)</div><div className="cot-bank-value cot-bank-mono">{empresa.cci || ''}</div></div>
                  <div className="cot-bank-divider" />
                  <div className="cot-bank-section"><div className="cot-bank-sublabel">Transferencia Internacional (SWIFT)</div><div className="cot-bank-label">Código SWIFT/BIC</div><div className="cot-bank-value cot-bank-mono">{empresa.swift || ''}</div></div>
                </div>
              </div>
              <div className="cot-totals-box">
                <div className="cot-totals-row"><span>{isEquipo ? 'Subtotal Equipo' : 'Subtotal'}</span><span>{formatCurrency(cot.subtotal, cot.moneda)}</span></div>
                <div className="cot-totals-row"><span>IGV ({cot.igv_porcentaje}%)</span><span>{formatCurrency(cot.igv_monto, cot.moneda)}</span></div>
                {tier.label !== 'Sin descuento' ? (
                  <div className="cot-totals-row cot-discount-row">
                    <span className="cot-discount-label"><span className="cot-discount-badge" style={{ background: tier.color }}>{tier.emoji} {tier.label}</span><span className="cot-discount-pct">-{cot.descuento_porcentaje}%</span></span>
                    <span>-{formatCurrency(cot.descuento_monto, cot.moneda)}</span>
                  </div>
                ) : (
                  <div className="cot-totals-row"><span>Descuento 0%</span><span>{formatCurrency(0, cot.moneda)}</span></div>
                )}
                <div className="cot-totals-row total" style={{ background: pc }}><span>{isEquipo ? 'INVERSIÓN MENSUAL' : 'TOTAL GENERAL'}</span><span>{formatCurrency(cot.total, cot.moneda)}</span></div>
              </div>
            </div>

            {secciones.length > 0 && <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>}
          </div>

          <div className="cot-footer" style={{ background: pc }}>
            <span>{empresa.nombre || '1Conecta'}</span>
            <span>{empresa.ubicacion || ''} | {empresa.telefono || ''}</span>
            <span>{empresa.website || ''}</span>
          </div>
        </div>

        {/* INDEX PAGE */}
        {secciones.length > 0 && (
          <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h2 className="cot-page-title">Cotización de Servicios</h2>
              <div className="cot-index-meta">
                <p>Fecha de Emisión: <em>{formatDate(cot.fecha_emision)}</em></p>
                <p>Proyecto/Servicio: <em>{proyectoNombre}</em></p>
              </div>
              <div className="cot-index-box">
                <h3>Índice</h3>
                {secciones.map((s, i) => <div key={i} className="cot-index-item"><strong>{s.titulo}</strong><span className="cot-index-dots" /><span className="cot-index-page">{i + 3}</span></div>)}
              </div>
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre}</span><span>Pág. 2</span><span>{empresa.website}</span></div>
          </div>
        )}

        {/* SECTION PAGES */}
        {secciones.map((sec, idx) => (
          <div key={idx} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body">
              <h3 className="cot-section-title" style={{ borderColor: pc }}>{sec.titulo}</h3>
              <div className="cot-section-content" dangerouslySetInnerHTML={{ __html: processContent(sec.contenido) }} />
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre}</span><span>Pág. {idx + 3}</span><span>{empresa.website}</span></div>
          </div>
        ))}
      </div>
    </div>
  )
}
